import {
  ComponentProps,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import clsx from 'clsx'
import { observer } from 'mobx-react-lite'

import { RoutingPaths } from 'app/routes/routes'

import {
  type BookingFormValues,
  getContactPersonConfig,
} from 'pages/kyrgyzstan/booking/model'
import { BookingContactPersonCard } from 'pages/kyrgyzstan/booking/ui/booking-contact-person-card'
import { BookingDetailsCard } from 'pages/kyrgyzstan/booking/ui/booking-details-card'
import { BookingPaymentMethodCard } from 'pages/kyrgyzstan/booking/ui/booking-payment-method-card'
import { BookingSummaryCard } from 'pages/kyrgyzstan/booking/ui/booking-summary-card'

import {
  KyrgyzstanEmpty,
  KyrgyzstanLoader,
  clearSearchFormState,
  hasCompleteKyrgyzstanBookingParams,
  kyrgyzstanDetailContextStore,
  kyrgyzstanDetailSelectionStore,
  kyrgyzstanSearchStore,
  restoreSearchFormState,
} from 'entities/kyrgyzstan'
import userStore from 'entities/user/module/user-store'

import type { PaymentStatus } from 'shared/api/kyrgyzstan/orders'
import ErrorImage from 'shared/images/error.png'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'
import { HeaderGoBack } from 'shared/ui/header-go-back'
import { PopupContent } from 'shared/ui/poup-content'

import {
  emptyBookingContactDefaultValues,
  getBookingContactDefaultValues,
  getBookingPaymentOptions,
  getBookingPriceParams,
  useBookingPriceQuote,
  useSubmitBooking,
} from './lib'
import s from './styles.module.scss'

const KyrgyzstanBookingPage = observer(() => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { openDialog, closeDialog } = useDialogBottomSheet()
  const lastShownErrorRef = useRef<string | null>(null)
  const contactRef = useRef<HTMLDivElement>(null)
  const summarySpacerRef = useRef<HTMLDivElement>(null)
  const expandedViewportHeight = useRef<number | null>(null)
  const [isEditing, setIsEditing] = useState(false)

  const { account: user } = userStore
  const contactDefaultValues = useMemo(
    () =>
      getBookingContactDefaultValues({
        user,
      }),
    [user],
  )

  const formMethods = useForm<BookingFormValues>({
    defaultValues: emptyBookingContactDefaultValues,
  })
  const {
    handleSubmit,
    reset,
    formState: { isDirty },
  } = formMethods
  const [selectedPayment, setSelectedPayment] = useState<PaymentStatus>('full')
  const [isBookingStateRestored, setIsBookingStateRestored] = useState(false)

  const { selectedParent, effectiveRealty } = kyrgyzstanDetailSelectionStore
  const { detailSearchParams } = kyrgyzstanDetailContextStore
  const hasBookingParams =
    hasCompleteKyrgyzstanBookingParams(detailSearchParams)
  const contactPersonConfig = getContactPersonConfig(effectiveRealty?.source)
  const ratehawkBookHash = effectiveRealty?.ratehawk_book_hash
  const priceParams = getBookingPriceParams({
    realtyId: effectiveRealty?.id,
    detailSearchParams,
    ratehawkBookHash,
  })

  const showBookingErrorDialog = useCallback(
    (errorText?: string, onAction?: () => void) => {
      const normalizedErrorText =
        errorText?.trim() || t('kyrgyzstan_block.booking.errors.price_failed')

      if (lastShownErrorRef.current === normalizedErrorText) return

      lastShownErrorRef.current = normalizedErrorText
      openDialog({
        padding: [20],
        isDialog: false,
        child: (
          <PopupContent
            image={ErrorImage}
            title={t('error')}
            button={t('try_again')}
            onClick={() => {
              lastShownErrorRef.current = null
              closeDialog()
              onAction?.()
            }}
            description={normalizedErrorText}
          />
        ),
      })
    },
    [closeDialog, openDialog, t],
  )

  const {
    price,
    error: priceError,
    isLoading: isPriceLoading,
  } = useBookingPriceQuote({
    params: priceParams,
    enabled: Boolean(priceParams),
    onError: showBookingErrorDialog,
  })
  const { submitBooking, isLoading: isSubmitLoading } = useSubmitBooking({
    onError: showBookingErrorDialog,
  })
  const roomsCount = price?.roomsCount ?? 0
  const paymentOptions = price
    ? getBookingPaymentOptions({
        totalPrice: price.totalPrice,
        prepaymentPrice: price.prepaymentPrice,
        source: effectiveRealty?.source,
        mbonusAvailable: price.mbonusAvailable,
        mbonusAmount: price.mbonusAmount,
        t,
      })
    : []
  const hasPaymentOptions = paymentOptions.length > 0
  const effectiveSelectedPayment = paymentOptions.some(
    (option) => option.id === selectedPayment,
  )
    ? selectedPayment
    : paymentOptions[0]?.id
  const selectedPaymentOption = paymentOptions.find(
    (option) => option.id === effectiveSelectedPayment,
  )
  const totalAmount = selectedPaymentOption?.amount ?? 0
  const isPaymentDisabled =
    isPriceLoading || isSubmitLoading || paymentOptions.length <= 1
  const isSubmitDisabled =
    isPriceLoading ||
    isSubmitLoading ||
    !effectiveSelectedPayment ||
    !price ||
    Boolean(priceError) ||
    totalAmount <= 0 ||
    !hasBookingParams

  const restoreSummary = useCallback(() => {
    if (contactRef.current?.contains(document.activeElement)) return

    const height = window.visualViewport?.height ?? window.innerHeight
    if (height >= (expandedViewportHeight.current ?? height) - 8) {
      expandedViewportHeight.current = null
      setIsEditing(false)
    }
  }, [])

  useEffect(() => {
    if (!isEditing) return

    window.visualViewport?.addEventListener('resize', restoreSummary)
    window.addEventListener('resize', restoreSummary)

    return () => {
      window.visualViewport?.removeEventListener('resize', restoreSummary)
      window.removeEventListener('resize', restoreSummary)
    }
  }, [isEditing, restoreSummary])

  useLayoutEffect(() => {
    const spacer = summarySpacerRef.current
    const summary = spacer?.firstElementChild
    if (!hasPaymentOptions || !spacer || !(summary instanceof HTMLElement))
      return

    const updateSummaryHeight = () => {
      const height = summary.getBoundingClientRect().height
      if (height > 0)
        spacer.style.setProperty('--summary-height', `${height}px`)
    }

    updateSummaryHeight()
    const observer = new ResizeObserver(updateSummaryHeight)
    observer.observe(summary)

    return () => observer.disconnect()
  }, [hasPaymentOptions])

  useEffect(() => {
    restoreSearchFormState(kyrgyzstanSearchStore)
    clearSearchFormState()
    kyrgyzstanDetailContextStore.restoreSavedContext()
    kyrgyzstanDetailSelectionStore.restoreSelection()
    setIsBookingStateRestored(true)
  }, [])

  useEffect(() => {
    if (isDirty) return

    reset(contactDefaultValues)
  }, [contactDefaultValues, isDirty, reset])

  const handleGoBack = () => {
    navigate(-1)
  }

  const handleOpenDetail = () => {
    if (!selectedParent) return

    navigate(RoutingPaths.kyrgyzstan.detailView.path(selectedParent.id))
  }

  const headerProps: ComponentProps<typeof HeaderGoBack> = {
    title: t('kyrgyzstan_block.booking.title'),
    onGoBack: handleGoBack,
    variant: 'white' as const,
    arrowColor: 'var(--inverse)' as const,
  }

  const onSubmit = (formData: BookingFormValues) => {
    submitBooking({
      formData,
      realtyId: effectiveRealty?.id,
      detailSearchParams,
      selectedPayment: effectiveSelectedPayment,
      ratehawkBookHash: price?.ratehawkBookHash,
    })
  }

  if (!isBookingStateRestored) {
    return (
      <div>
        <HeaderGoBack
          {...headerProps}
          buttonWhite
        />
        <KyrgyzstanLoader />
      </div>
    )
  }

  if (!selectedParent || !effectiveRealty) {
    return (
      <div>
        <HeaderGoBack
          {...headerProps}
          buttonWhite
        />
        <KyrgyzstanEmpty title={t('kyrgyzstan_block.error.title')} />
      </div>
    )
  }

  if (!hasBookingParams) {
    return (
      <div>
        <HeaderGoBack
          {...headerProps}
          buttonWhite
        />
        <KyrgyzstanEmpty title={t('kyrgyzstan_block.error.title')} />
      </div>
    )
  }

  return (
    <div className={clsx('container', s.container)}>
      <HeaderGoBack
        {...headerProps}
        buttonWhite
      />

      <FormProvider {...formMethods}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={s.content}
        >
          <BookingDetailsCard
            accommodation={{
              typeName: selectedParent.type.name,
              title: selectedParent.title,
              onClick: handleOpenDetail,
            }}
            roomsCount={roomsCount}
            isRoomsCountLoading={isPriceLoading}
            cancellation={{
              startDate: detailSearchParams?.start_date,
              freeCancellationMinutes: selectedParent.free_cancellation_minutes,
              arrivalTime: selectedParent.times?.arrival_time,
              timezone: selectedParent.timezone,
            }}
          />

          <div
            ref={contactRef}
            onFocusCapture={() => {
              expandedViewportHeight.current ??=
                window.visualViewport?.height ?? window.innerHeight
              setIsEditing(true)
            }}
            onBlurCapture={() => window.requestAnimationFrame(restoreSummary)}
          >
            <BookingContactPersonCard
              config={contactPersonConfig}
              isDisabled={isSubmitLoading}
            />
          </div>

          {(isPriceLoading || hasPaymentOptions) && (
            <BookingPaymentMethodCard
              options={paymentOptions}
              selectedPayment={effectiveSelectedPayment}
              isDisabled={isPaymentDisabled}
              isLoading={isPriceLoading}
              onSelect={setSelectedPayment}
            />
          )}

          {hasPaymentOptions && (
            <div
              ref={summarySpacerRef}
              className={s.summarySpacer}
              hidden={isEditing}
            >
              <BookingSummaryCard
                amount={totalAmount}
                isLoading={isSubmitLoading}
                isSubmitDisabled={isSubmitDisabled}
              />
            </div>
          )}
        </form>
      </FormProvider>
    </div>
  )
})

export default KyrgyzstanBookingPage
